import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  clearUserInfo,
  sessionEndedElsewhere,
  setToken,
  setUserInfo,
} from "../store/auth/authSlice";
import { useLazyGetCurrentSessionQuery } from "../store/auth/usersApiSlice";
import {
  AUTH_EVENTS,
  subscribeToAuthEvents,
} from "../utils/authChannel";

/* One probe per page load, shared by every caller. */
let bootstrapPromise = null;
let bootstrapSettled = false;

/** Test seam — forget the shared probe so the next mount runs a fresh one. */
export const __resetSessionBootstrap = () => {
  bootstrapPromise = null;
  bootstrapSettled = false;
};

/* Make a newly opened tab inherit the session, and keep every tab in step. */
export const useSessionBootstrap = () => {
  const dispatch = useDispatch();
  const [probeSession] = useLazyGetCurrentSessionQuery();

  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const currentEmail = useSelector((state) => state.auth.userInfo?.email ?? null);

  // `ready` gates the first paint. Without it a protected route renders the
  // login redirect for the split second before the probe answers, which is a
  // visible flash of the login screen on every tab open.
  // Already answered earlier in this page's life: start ready, so a later
  // mount does not flash a loading state over a session we know about.
  const [ready, setReady] = useState(bootstrapSettled);
  const emailRef = useRef(currentEmail);
  emailRef.current = currentEmail;
  const loggedInRef = useRef(isLoggedIn);
  loggedInRef.current = isLoggedIn;

  useEffect(() => {
    let cancelled = false;

    if (!bootstrapPromise) {
      bootstrapPromise = (async () => {
        try {
          const result = await probeSession().unwrap();

          if (result?.authenticated) {
            dispatch(setUserInfo(result.user));
            dispatch(setToken());
          }
        } catch {
          // A 401 here is an ANSWER — "not signed in" — not an error. The base
          // query deliberately does not raise the login modal for this call.
          if (loggedInRef.current) {
            // The store claimed a session the cookies do not back. That is a
            // stale mirror from a previous build or an expired session, and
            // leaving it would render a signed-in shell over 401s.
            dispatch(clearUserInfo());
          }
        } finally {
          bootstrapSettled = true;
        }
      })();
    }

    bootstrapPromise.then(() => {
      if (!cancelled) setReady(true);
    });

    return () => {
      cancelled = true;
    };
    // Deliberately once per mount: this is a bootstrap, not a subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return subscribeToAuthEvents((event) => {
      if (event.type === AUTH_EVENTS.SIGNED_OUT) {
        dispatch(sessionEndedElsewhere({ identityChanged: false }));
        return;
      }

      if (event.type === AUTH_EVENTS.SIGNED_IN) {
        const incoming = event.identity ? String(event.identity).toLowerCase() : null;
        const existing = emailRef.current
          ? String(emailRef.current).toLowerCase()
          : null;

        if (existing && incoming && existing !== incoming) {
          // A DIFFERENT user took over this browser. Without the purge this
          // tab keeps showing user A's cached company data while tab 1 is
          // user B — the TIP-511 leak, arriving through a broadcast instead
          // of through a re-login in this tab.
          dispatch(sessionEndedElsewhere({ identityChanged: true }));
        }

        // Re-read who we are from the server rather than trusting the event.
        bootstrapSettled = true;
        probeSession()
          .unwrap()
          .then((result) => {
            if (result?.authenticated) {
              dispatch(setUserInfo(result.user));
              dispatch(setToken());
            }
          })
          .catch(() => {
            /* Nothing to adopt. */
          });
      }

      // `refreshed` needs no action: the cookies are already updated for the
      // whole browser, and nothing in the store mirrors them any more.
    });
  }, [dispatch, probeSession]);

  return ready;
};
